import React, { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { 
  Briefcase, 
  Heart, 
  Clock, 
  Award, 
  Users,
  CheckCircle
} from "lucide-react";
import { CareerModal } from "./CareerModal";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// All job details with descriptions
const allJobDetails = {
  "Care Assistant": {
    description: "Join our caring team and make a real difference in residents' lives. No experience necessary - full training provided.",
    requirements: ["Compassionate nature", "Good communication", "Flexible availability"],
    benefits: ["Competitive salary", "Training provided", "Career progression"]
  },
  "Senior Care Assistant": {
    description: "Take the next step in your care career with a senior role, mentoring team members and leading shifts.",
    requirements: ["NVQ Level 3 or equivalent", "2+ years experience", "Leadership skills"],
    benefits: ["Enhanced pay", "Leadership training", "Career growth"]
  },
  "Registered Nurse": {
    description: "We're looking for qualified nurses to join our nursing team, providing high-quality care to our residents.",
    requirements: ["NMC Registration", "UK work permit", "Nursing experience preferred"],
    benefits: ["Excellent rates", "Supportive team", "Professional development"]
  },
  "Night Care Assistant": {
    description: "Provide overnight care and support to our residents in a calm, nurturing environment.",
    requirements: ["Compassionate nature", "Ability to work nights", "Good communication"],
    benefits: ["Night shift premium", "Training provided", "Supportive team"]
  },
  "Kitchen Assistant": {
    description: "Help prepare nutritious meals for our residents in our modern kitchen facility.",
    requirements: ["Basic food hygiene", "Team player", "Reliable"],
    benefits: ["Meals provided", "Regular hours", "Friendly team"]
  },
  "Housekeeper": {
    description: "Maintain high cleanliness standards throughout our home, ensuring a comfortable environment.",
    requirements: ["Attention to detail", "Physical fitness", "Reliable"],
    benefits: ["Regular hours", "Supportive environment", "Part of care team"]
  },
  "Housekeeping": {
    description: "Keep our nursing home clean, safe and welcoming for all residents and visitors.",
    requirements: ["Attention to detail", "Physical fitness", "Team player"],
    benefits: ["Flexible hours", "Training provided", "Friendly environment"]
  },
  "Activities Coordinator": {
    description: "Plan and deliver engaging activities that enhance our residents' quality of life and wellbeing.",
    requirements: ["Creative thinking", "Excellent communication", "Experience with elderly care preferred"],
    benefits: ["Rewarding role", "Creative freedom", "Make a real difference"]
  },
  "Manager": {
    description: "Lead our dedicated team to deliver outstanding care. Oversee daily operations and staff development.",
    requirements: ["Management experience", "Healthcare background", "Strong leadership"],
    benefits: ["Competitive salary", "Career development", "Supportive organisation"]
  },
  "Maintenance": {
    description: "Keep our facilities in excellent condition, ensuring a safe and comfortable environment for residents.",
    requirements: ["Practical skills", "Problem solver", "Health & Safety awareness"],
    benefits: ["Varied work", "Autonomy", "Supportive team"]
  },
  "Chef": {
    description: "Create delicious, nutritious meals tailored to our residents' dietary needs and preferences.",
    requirements: ["Cooking qualifications", "Food hygiene certificate", "Menu planning experience"],
    benefits: ["Creative freedom", "Regular hours", "Quality ingredients"]
  },
  "Admin": {
    description: "Provide essential administrative support to keep our nursing home running smoothly.",
    requirements: ["Computer literacy", "Organised", "Good communication"],
    benefits: ["Office environment", "Regular hours", "Part of care team"]
  }
};

const benefits = [
  { icon: Heart, title: "Rewarding Work", desc: "Make a real difference every day" },
  { icon: Clock, title: "Flexible Hours", desc: "Full-time, part-time & bank shifts" },
  { icon: Award, title: "Training & Development", desc: "NVQ qualifications supported" },
  { icon: Users, title: "Supportive Team", desc: "Friendly, inclusive workplace" }
];

export const Careers = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [availableJobs, setAvailableJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAvailableRoles();
  }, []);

  const fetchAvailableRoles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/jobs/roles/available`);
      // Map API response to include full job details
      const jobs = response.data.map(role => ({
        id: role.id,
        title: role.title,
        type: role.type,
        ...allJobDetails[role.title] || {
          description: `Join our team as a ${role.title}.`,
          requirements: ["Compassionate nature", "Good communication", "Reliable"],
          benefits: ["Competitive salary", "Training provided", "Supportive team"]
        }
      }));
      setAvailableJobs(jobs);
    } catch (error) {
      console.error("Error fetching available roles:", error);
      // Fallback to default if API fails
      setAvailableJobs([
        {
          id: "1",
          title: "Care Assistant",
          type: "Full-time / Part-time",
          ...allJobDetails["Care Assistant"]
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const openApplicationModal = (role = "") => {
    setSelectedRole(role);
    setIsModalOpen(true);
  };

  return (
    <section id="careers" className="py-20 bg-gradient-to-b from-orange-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-orange-500 text-white hover:bg-orange-600">
            <Briefcase className="h-4 w-4 mr-2" />
            Join Our Team
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Careers at Barnston Court
          </h2>
          <div className="w-24 h-1 bg-teal-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Be part of a caring team that makes a real difference in people's lives every day
          </p>
        </div>

        {/* Benefits */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((benefit, index) => (
            <Card key={index} className="border-none shadow-lg text-center hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="bg-teal-100 text-teal-600 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="h-8 w-8" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Job Openings */}
        <div className="max-w-4xl mx-auto mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Current Openings
          </h3>
          
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-600">Loading available positions...</p>
            </div>
          ) : availableJobs.length === 0 ? (
            <Card className="border-2 border-gray-200">
              <CardContent className="pt-8 pb-8 text-center">
                <p className="text-gray-600">No positions currently available. Please check back later or submit a general application below.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {availableJobs.map((job) => (
                <Card 
                  key={job.id} 
                  className="border-2 border-teal-200 hover:border-teal-400 transition-colors"
                >
                  <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-bold text-xl text-gray-900">{job.title}</h4>
                          <Badge className="bg-teal-100 text-teal-700">{job.type}</Badge>
                        </div>
                        <p className="text-gray-700 mb-4">{job.description}</p>
                        
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <h5 className="font-semibold text-sm text-teal-700 mb-2">Requirements:</h5>
                            <ul className="space-y-1">
                              {job.requirements.map((req, i) => (
                                <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                                  <CheckCircle className="h-4 w-4 text-teal-500" />
                                  {req}
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h5 className="font-semibold text-sm text-orange-600 mb-2">Benefits:</h5>
                            <ul className="space-y-1">
                              {job.benefits.map((ben, i) => (
                                <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                                  <CheckCircle className="h-4 w-4 text-orange-500" />
                                  {ben}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        onClick={() => openApplicationModal(job.title)}
                        className="bg-orange-500 hover:bg-orange-600 text-white whitespace-nowrap"
                        data-testid={`apply-btn-${job.id}`}
                      >
                        Apply Now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* General Application CTA */}
        <Card className="max-w-3xl mx-auto border-none shadow-xl bg-gradient-to-r from-teal-600 to-teal-700 text-white">
          <CardContent className="pt-8 pb-8 text-center">
            <h3 className="text-2xl font-bold mb-3">Don't See Your Role?</h3>
            <p className="text-teal-100 mb-6">
              We're always looking for caring individuals. Send us your details and we'll keep you in mind for future opportunities.
            </p>
            <Button
              onClick={() => openApplicationModal()}
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="general-apply-btn"
            >
              Submit General Application
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Career Application Modal */}
      <CareerModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        selectedRole={selectedRole}
      />
    </section>
  );
};
